import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Menu,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  CalendarToday as CalendarTodayIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon,
  KeyboardDoubleArrowRight as KeyboardDoubleArrowRightIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import DateRangeCalendar, { DateRangeButton } from "../calendar/Calendar";
import { format } from "date-fns";
// 재계약 모달 컴포넌트 import
import ContractRenewModal from "./ContractRenewModal";

const ContractList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [activeStatusFilter, setActiveStatusFilter] = useState("active");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortBy, setSortBy] = useState("createdAt"); // 정렬 기준 필드 추가
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContractId, setSelectedContractId] = useState(null);

  // 기간 필터 상태 추가
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [dateFilterActive, setDateFilterActive] = useState(false);

  // 이번 달 보험 종료 필터 상태 추가
  const [currentMonthExpiryFilter, setCurrentMonthExpiryFilter] =
    useState(false);

  // 페이징 상태 추가
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 상태 코드 데이터
  const [statusCodes, setStatusCodes] = useState([]);

  // 재계약 모달 상태 추가
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // 알림 스낵바 상태 추가
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // 비활성화 다이얼로그 상태 추가
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  // 비활성화 시 함께 변경할 계약 상태 ("" = 상태 유지)
  const [cancelStatusCode, setCancelStatusCode] = useState("");

  // 활성화 다이얼로그 상태 추가
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  // 활성화 시 되돌릴 계약 상태 ("" = 상태 유지)
  const [activateStatusCode, setActivateStatusCode] = useState("");

  // 비활성화된 계약의 상태 변경 다이얼로그 상태
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [statusChangeCode, setStatusChangeCode] = useState("");
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // 비활성화 시 선택 가능한 상태 / 활성화 시 되돌릴 수 있는 상태
  const DEACTIVATE_STATUS_OPTIONS = [
    { codeId: "001002_0013", label: "사업장변경" },
    { codeId: "001002_0014", label: "재계약포기" },
  ];
  const ACTIVATE_STATUS_OPTIONS = [
    { codeId: "001002_0003", label: "서명 대기" },
    { codeId: "001002_0004", label: "서명 진행중" },
    { codeId: "001002_0001", label: "승인 대기" },
    { codeId: "001002_0002", label: "계약 완료" },
  ];
  // 사업장변경/재계약포기 상태인 계약은 활성화 시 되돌릴 상태 선택이 필수
  const isClosedStatusContract = ["001002_0013", "001002_0014"].includes(
    selectedContract?.statusCodeId
  );

  // URL<->상태 동기화 루프 방지 플래그
  const syncingFromUrlRef = useRef(false);

  // URL 쿼리(page/size) -> 상태 동기화
  useEffect(() => {
    const pageParam = parseInt(searchParams.get("page"), 10);
    const sizeParam = parseInt(searchParams.get("size"), 10);

    const hasPage = Number.isFinite(pageParam) && pageParam >= 1;
    const hasSize = Number.isFinite(sizeParam);

    const nextPage = hasPage ? pageParam - 1 : 0; // URL은 1-base, 상태는 0-base
    const nextSize = hasSize ? sizeParam : 10;

    let changed = false;
    if (page !== nextPage) {
      setPage(nextPage);
      changed = true;
    }
    if (size !== nextSize) {
      setSize(nextSize);
      changed = true;
    }

    if (changed) {
      // URL을 반영해 상태를 바꾼 경우, 다음 사이클의 상태->URL 동기화를 건너뜀
      syncingFromUrlRef.current = true;
    }
  }, [searchParams]);

  // 상태(page/size) -> URL 쿼리 동기화
  useEffect(() => {
    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false;
      return;
    }

    const params = new URLSearchParams(searchParams);
    const desiredPage = (page + 1).toString(); // URL은 1-base 노출
    const desiredSize = size.toString();

    const currentUrlPage = params.get("page");
    const currentUrlSize = params.get("size");

    let changed = false;
    if (currentUrlPage !== desiredPage) {
      params.set("page", desiredPage);
      changed = true;
    }
    if (currentUrlSize !== desiredSize) {
      params.set("size", desiredSize);
      changed = true;
    }

    if (changed) {
      setSearchParams(params, { replace: true });
    }
  }, [page, size, searchParams, setSearchParams]);

  // 계약 목록 조회
  const fetchContracts = async () => {
    try {
      setLoading(true);

      // URL 파라미터 구성
      const params = new URLSearchParams();

      // 기본 파라미터
      params.append("status", activeStatusFilter);
      params.append("page", page.toString());
      params.append("size", size.toString());
      params.append("sortBy", sortBy);
      params.append("sortDirection", sortOrder);

      // 통합 검색어 필터링을 백엔드에서 처리
      if (searchQuery) {
        // 새로운 searchQuery 파라미터 사용 (OR 조건으로 여러 필드 검색)
        params.append("searchQuery", searchQuery);
      }

      // 상태 필터링을 백엔드에서 처리
      if (statusFilter) {
        params.append("statusName", statusFilter);
      }

      // 날짜 범위 필터링
      if (dateFilterActive && startDate && endDate) {
        const formatDate = (date) => {
          const d = new Date(date);
          return d.toISOString().split("T")[0]; // YYYY-MM-DD 형식
        };
        params.append("insuranceStartDateFrom", formatDate(startDate));
        params.append("insuranceStartDateTo", formatDate(endDate));
      }

      // 이번 달 보험 만료 필터링
      if (currentMonthExpiryFilter) {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();

        // 이번 달의 첫째 날과 마지막 날
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);

        const formatDate = (date) => date.toISOString().split("T")[0];
        params.append("insuranceEndDateFrom", formatDate(firstDay));
        params.append("insuranceEndDateTo", formatDate(lastDay));
      }

      const apiUrl = `https://contract.tirebank.kr/api/contracts/with-trustee?${params.toString()}`;

      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("계약 목록 조회 실패");
      const data = await response.json();

      // 페이징 응답 데이터 처리
      setContracts(data.content || []);
      setFilteredContracts(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error("계약 목록 조회 중 오류:", error);
    } finally {
      setLoading(false);
    }
  };

  // 상태 코드 그룹 조회
  const fetchStatusCodes = async () => {
    try {
      const response = await fetch(
        "https://contract.tirebank.kr/api/codes/groups/001002/codes"
      );
      if (!response.ok) throw new Error("상태 코드 조회 실패");
      const data = await response.json();
      // sortOrder 기준으로 정렬
      setStatusCodes(data.sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (error) {
      console.error("상태 코드 조회 중 오류:", error);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchStatusCodes(); // 상태 코드 조회 추가
  }, [
    activeStatusFilter,
    page,
    size,
    searchQuery,
    statusFilter,
    sortOrder,
    sortBy,
    dateFilterActive,
    startDate,
    endDate,
    currentMonthExpiryFilter,
  ]);

  // 업체명 목록 설정 (현재 페이지의 계약에서만 추출)
  useEffect(() => {
    if (contracts.length > 0) {
      const uniqueCompanies = [
        ...new Set(contracts.map((contract) => contract.companyName)),
      ].filter(Boolean);
      setCompanyOptions(uniqueCompanies);
    }
  }, [contracts]);

  // 백엔드에서 모든 필터링이 처리되므로 클라이언트 필터링 불필요
  useEffect(() => {
    setFilteredContracts(contracts);
  }, [contracts]);

  const handleMenuClick = (event, contractId) => {
    setAnchorEl(event.currentTarget);
    setSelectedContractId(contractId);

    // 선택된 계약 정보 저장
    const contract = contracts.find((c) => c.id === contractId);
    if (contract) {
      setSelectedContract(contract);

      // 수정: with-trustee API에서는 company 객체가 별도로 없고 계약 객체에 통합되어 있음
      // 수탁업체 정보를 직접 생성하여 설정
      const companyInfo = {
        id: contract.companyId,
        storeName: contract.storeName || contract.companyName,
        trustee: contract.trustee,
        companyName: contract.companyName,
        // 필요한 다른 수탁업체 관련 필드들 추가
        businessNumber: contract.businessNumber,
        representativeName: contract.representativeName,
      };

      setSelectedCompany(companyInfo);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedContractId(null);
  };

  // 재계약 모달 열기
  const handleOpenRenewModal = () => {
    const contract = selectedContract;
    const company = selectedCompany;

    if (!contract) {
      showSnackbar("계약 정보를 찾을 수 없습니다.", "error");
      return;
    }

    if (!company || !company.id) {
      showSnackbar("수탁업체 정보를 찾을 수 없습니다.", "error");
      return;
    }

    setRenewModalOpen(true);
    handleMenuClose();
  };

  // 재계약 모달 닫기
  const handleCloseRenewModal = () => {
    setRenewModalOpen(false);
  };

  // 재계약 성공 처리
  const handleRenewSuccess = (result) => {
    showSnackbar("재계약이 성공적으로 신청되었습니다.", "success");
    fetchContracts(); // 계약 목록 새로고침
  };

  // 비활성화 다이얼로그 열기
  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
    setCancelReason("");
    setCancelStatusCode("");
    setAnchorEl(null); // 메뉴만 닫고 selectedContractId는 유지
  };

  // 비활성화 다이얼로그 닫기
  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelStatusCode("");
    setSelectedContractId(null);
  };

  // 비활성화 처리
  const handleCancelContract = async () => {
    if (!selectedContractId) {
      showSnackbar("계약 정보가 없습니다.", "error");
      return;
    }

    try {
      setCancelLoading(true);

      // 상태를 함께 변경하는 경우 사유에 상태명을 포함해 이력에 남김
      const statusLabel = DEACTIVATE_STATUS_OPTIONS.find(
        (o) => o.codeId === cancelStatusCode
      )?.label;
      let reasonText = cancelReason || "관리자 비활성화";
      if (statusLabel) {
        reasonText = cancelReason
          ? `[${statusLabel}] ${cancelReason}`
          : statusLabel;
      }
      const statusParam = cancelStatusCode
        ? `&statusCodeId=${encodeURIComponent(cancelStatusCode)}`
        : "";

      const response = await fetch(
        `https://contract.tirebank.kr/api/contracts/${selectedContractId}?reason=${encodeURIComponent(
          reasonText
        )}${statusParam}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error("비활성화에 실패했습니다.");
      }

      showSnackbar("계약이 성공적으로 비활성화되었습니다.", "success");
      handleCloseCancelDialog();
      fetchContracts(); // 계약 목록 새로고침
    } catch (error) {
      console.error("비활성화 오류:", error);
      showSnackbar(
        error.message || "비활성화 중 오류가 발생했습니다.",
        "error"
      );
    } finally {
      setCancelLoading(false);
    }
  };

  // 활성화 다이얼로그 열기
  const handleOpenActivateDialog = () => {
    setActivateDialogOpen(true);
    setActivateStatusCode("");
    setAnchorEl(null); // 메뉴만 닫고 selectedContractId는 유지
  };

  // 활성화 다이얼로그 닫기
  const handleCloseActivateDialog = () => {
    setActivateDialogOpen(false);
    setActivateStatusCode("");
    setSelectedContractId(null);
  };

  // 활성화 처리
  const handleActivateContract = async () => {
    if (!selectedContractId) {
      showSnackbar("계약 정보가 없습니다.", "error");
      return;
    }

    try {
      setActivateLoading(true);

      const response = await fetch(
        `https://contract.tirebank.kr/api/contracts/${selectedContractId}/reactivate${
          activateStatusCode
            ? `?statusCodeId=${encodeURIComponent(activateStatusCode)}`
            : ""
        }`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        throw new Error("활성화에 실패했습니다.");
      }

      showSnackbar("계약이 성공적으로 활성화되었습니다.", "success");
      handleCloseActivateDialog();
      fetchContracts(); // 계약 목록 새로고침
    } catch (error) {
      console.error("활성화 오류:", error);
      showSnackbar(error.message || "활성화 중 오류가 발생했습니다.", "error");
    } finally {
      setActivateLoading(false);
    }
  };

  // 비활성화된 계약 상태 변경 다이얼로그 열기 (현재 상태가 해당되면 미리 선택)
  const handleOpenStatusChangeDialog = () => {
    const currentCode = selectedContract?.statusCodeId;
    setStatusChangeCode(
      DEACTIVATE_STATUS_OPTIONS.some((o) => o.codeId === currentCode)
        ? currentCode
        : ""
    );
    setStatusChangeDialogOpen(true);
    setAnchorEl(null); // 메뉴만 닫고 selectedContractId는 유지
  };

  // 비활성화된 계약 상태 변경 다이얼로그 닫기
  const handleCloseStatusChangeDialog = () => {
    setStatusChangeDialogOpen(false);
    setStatusChangeCode("");
    setSelectedContractId(null);
  };

  // 비활성화된 계약 상태 변경 처리
  const handleChangeClosedStatus = async () => {
    if (!selectedContractId || !statusChangeCode) {
      showSnackbar("계약 정보 또는 상태가 선택되지 않았습니다.", "error");
      return;
    }

    try {
      setStatusChangeLoading(true);

      const response = await fetch(
        `https://contract.tirebank.kr/api/contracts/${selectedContractId}/closed-status?statusCodeId=${encodeURIComponent(
          statusChangeCode
        )}`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        throw new Error("상태 변경에 실패했습니다.");
      }

      showSnackbar("계약 상태가 변경되었습니다.", "success");
      handleCloseStatusChangeDialog();
      fetchContracts(); // 계약 목록 새로고침
    } catch (error) {
      console.error("상태 변경 오류:", error);
      showSnackbar(
        error.message || "상태 변경 중 오류가 발생했습니다.",
        "error"
      );
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // 스낵바 표시 함수
  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // 스낵바 닫기
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // 계약 상태에 따른 Chip 컴포넌트 렌더링 (수정: statusCode와 statusName 사용)
  const renderStatusChip = (contract) => {
    // 기본 상태 설정 (fallback)
    let label = "계약 대기";
    let color = "#666";
    let bgColor = "#F5F5F5";

    // statusCode와 statusName 있는 경우 우선 사용
    if (contract.statusName) {
      label = contract.statusName;

      // 상태 코드에 따른 스타일 지정
      if (contract.statusCodeId === "001002_0001") {
        // 승인대기 (계약자 동작 - 주황색)
        color = "#FF9800";
        bgColor = "#FFF3E0";
      } else if (contract.statusCodeId === "001002_0002") {
        // 계약완료 (파란색)
        color = "#3182F6";
        bgColor = "#E3F2FD";
      } else if (contract.statusCodeId === "001002_0003") {
        // 서명대기 (관리자 동작 - 초록색)
        color = "#4CAF50";
        bgColor = "#E8F5E9";
      } else if (contract.statusCodeId === "001002_0004") {
        // 서명진행중 (계약자 동작 - 초록색)
        color = "#4CAF50";
        bgColor = "#E8F5E9";
      } else if (contract.statusCodeId === "001002_0008") {
        // 재서명 요청 (관리자 동작 - 초록색)
        color = "#4CAF50";
        bgColor = "#E8F5E9";
      } else if (contract.statusCodeId === "001002_0009") {
        // 재서명 진행중 (관리자 동작 - 초록색)
        color = "#4CAF50";
        bgColor = "#E8F5E9";
      } else if (contract.statusCodeId === "001002_0010") {
        // 반려 (빨간색)
        color = "#F44336";
        bgColor = "#FFEBEE";
      } else if (contract.statusCodeId === "001002_0011") {
        // 결재 대기 (결재자2 동작 - 보라색)
        color = "#9C27B0";
        bgColor = "#F3E5F5";
      } else if (contract.statusCodeId === "001002_0012") {
        // 결재 반려 (결재자1 동작 - 주황색)
        color = "#F44336";
        bgColor = "#FFEBEE";
      } else if (
        contract.statusCodeId === "001002_0013" ||
        contract.statusCodeId === "001002_0014"
      ) {
        // 사업장변경, 재계약포기 (종료 - 회색)
        color = "#757575";
        bgColor = "#EEEEEE";
      }
    } else {
      // 기존 로직 (fallback)
      if (contract.progressRate === 100) {
        label = "계약 완료";
        color = "#3182F6";
        bgColor = "#E3F2FD";
      } else if (contract.progressRate > 0) {
        label = "서명 진행중";
        color = "#4CAF50";
        bgColor = "#E8F5E9";
      }
    }

    return (
      <Box
        sx={{
          backgroundColor: bgColor,
          color: color,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          width: "fit-content",
          fontSize: "0.80rem",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    );
  };

  // 날짜 포맷 함수
  const formatDate = (dateString, dateOnly = false) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      // 날짜만 표시 (YYYY-MM-DD)
      if (dateOnly) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(date.getDate()).padStart(2, "0")}`;
      }

      // 날짜와 시간 모두 표시 (YY-MM-DD HH:MM)
      return `${date.getFullYear() - 2000}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(
        date.getHours()
      ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    } catch (error) {
      console.error("날짜 포맷 오류:", error, dateString);
      return "";
    }
  };

  // 계약 상세 페이지로 이동
  const handleContractClick = (contractId) => {
    navigate(`/contract-detail/${contractId}`);
  };

  // 필터 초기화
  const handleResetFilter = () => {
    setSearchQuery("");
    setStatusFilter("");
    setActiveStatusFilter("active");
    setStartDate(null);
    setEndDate(null);
    setDateFilterActive(false);
    setCurrentMonthExpiryFilter(false);
    setPage(0); // 페이지도 초기화
  };

  // 필터 변경 시 페이지를 첫 페이지로 리셋
  const resetPageOnFilterChange = () => {
    if (page !== 0) {
      setPage(0);
    }
  };

  // 필터 변경 감지
  useEffect(() => {
    resetPageOnFilterChange();
  }, [
    searchQuery,
    statusFilter,
    activeStatusFilter,
    dateFilterActive,
    currentMonthExpiryFilter,
    sortBy,
  ]);

  // 페이지 변경 핸들러
  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1); // Material-UI Pagination은 1부터 시작하므로 -1
  };

  // 페이지 크기 변경 핸들러
  const handlePageSizeChange = (event) => {
    setSize(parseInt(event.target.value));
    setPage(0); // 페이지 크기 변경 시 첫 페이지로
  };

  // 컬럼 정렬 핸들러
  const handleSort = (field) => {
    if (sortBy === field) {
      // 같은 필드를 클릭하면 정렬 방향 변경
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // 다른 필드를 클릭하면 해당 필드로 내림차순 정렬
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // 정렬 표시 컴포넌트
  const SortableHeader = ({ field, children, sortable = true }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: sortable ? "pointer" : "default",
        "&:hover": sortable ? { color: "#1976d2" } : {},
      }}
      onClick={sortable ? () => handleSort(field) : undefined}
    >
      <Typography variant="subtitle2" sx={{ color: "inherit" }}>
        {children}
      </Typography>
      {sortable && sortBy === field && (
        <Box sx={{ ml: 0.5, display: "flex", alignItems: "center" }}>
          {sortOrder === "asc" ? (
            <ArrowUpwardIcon sx={{ fontSize: 16, color: "#1976d2" }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 16, color: "#1976d2" }} />
          )}
        </Box>
      )}
    </Box>
  );

  // 날짜 다이얼로그 열기
  const handleOpenDateDialog = () => {
    setDateDialogOpen(true);
  };

  // 날짜 다이얼로그 닫기
  const handleCloseDateDialog = () => {
    setDateDialogOpen(false);
  };

  // 날짜 변경 핸들러
  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  // 날짜 필터 적용
  const handleApplyDateFilter = () => {
    if (startDate && endDate) {
      setDateFilterActive(true);
      handleCloseDateDialog();
    }
  };

  // 날짜 필터 초기화
  const handleResetDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setDateFilterActive(false);
    handleCloseDateDialog();
  };

  // 날짜 범위 텍스트 구성
  const getDateRangeText = () => {
    if (!dateFilterActive) return "전체";

    if (!startDate || !endDate) return "전체";

    try {
      const startFormatted = format(new Date(startDate), "yy-MM-dd");
      const endFormatted = format(new Date(endDate), "yy-MM-dd");
      return `${startFormatted} ~ ${endFormatted}`;
    } catch (error) {
      console.error("날짜 형식 오류:", error);
      return "전체";
    }
  };

  // 상태에 따른 색상 반환
  const getStatusColor = (statusName) => {
    switch (statusName) {
      case "작성중":
        return { bg: "#FFF7E6", text: "#D48806" };
      case "서명중":
        return { bg: "#E6F4FF", text: "#0958D9" };
      case "서명완료":
        return { bg: "#E6FFFB", text: "#08979C" };
      case "철회":
        return { bg: "#FFF1F0", text: "#F5222D" };
      case "만료":
        return { bg: "#F9F0FF", text: "#722ED1" };
      default:
        return { bg: "#F5F5F5", text: "#8C8C8C" };
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#F8F8FE", minHeight: "100vh" }}>
      {/* 상단 헤더 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#3A3A3A",
          }}
        >
          계약 관리
        </Typography>
      </Box>

      {/* 검색 및 필터 영역 */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        {/* 검색어 입력 */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{ mb: 1, color: "#666", display: "block" }}
          >
            검색어
          </Typography>
          <TextField
            placeholder="계약번호, 계약 제목 또는 수탁사업체명"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: "100%",
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#E0E0E0",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9E9E9E" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* 정렬 필터 */}
        <Box>
          <Typography
            variant="caption"
            sx={{ mb: 1, color: "#666", display: "block" }}
          >
            정렬
          </Typography>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              backgroundColor: "white",
            }}
          >
            <Select
              displayEmpty
              value={`${sortBy}_${sortOrder}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split("_");
                setSortBy(field);
                setSortOrder(direction);
              }}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
              }}
            >
              <MenuItem value="createdAt_desc">최신 계약순</MenuItem>
              <MenuItem value="createdAt_asc">오래된 계약순</MenuItem>
              <MenuItem value="contractNumber_asc">계약번호 순</MenuItem>
              <MenuItem value="title_asc">계약명 순</MenuItem>
              <MenuItem value="insuranceStartDate_desc">
                보험시작일 최신순
              </MenuItem>
              <MenuItem value="insuranceEndDate_desc">
                보험종료일 최신순
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* 상태 필터 */}
        <Box>
          <Typography
            variant="caption"
            sx={{ mb: 1, color: "#666", display: "block" }}
          >
            상태
          </Typography>
          <FormControl
            size="small"
            sx={{
              minWidth: 120,
              backgroundColor: "white",
            }}
          >
            <Select
              displayEmpty
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
              }}
            >
              <MenuItem value="">전체</MenuItem>
              {/* 코드 그룹 API로부터 가져온 상태 코드 목록 */}
              {statusCodes.map((code) => (
                <MenuItem key={code.codeId} value={code.codeName}>
                  {code.codeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* 활성화 상태 필터 (수정됨) */}
        <Box>
          <Typography
            variant="caption"
            sx={{ mb: 1, color: "#666", display: "block" }}
          >
            활성화 상태
          </Typography>
          <FormControl
            size="small"
            sx={{
              minWidth: 120,
              backgroundColor: "white",
            }}
          >
            <Select
              displayEmpty
              value={activeStatusFilter}
              onChange={(e) => setActiveStatusFilter(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
              }}
            >
              <MenuItem value="active">활성화</MenuItem>
              <MenuItem value="inactive">만료</MenuItem>
              <MenuItem value="all">전체</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* 기간 필터 */}
        <Box>
          <Typography
            variant="caption"
            sx={{ mb: 1, color: "#666", display: "block" }}
          >
            기간
          </Typography>
          <DateRangeButton
            startDate={startDate}
            endDate={endDate}
            isActive={dateFilterActive}
            onClick={handleOpenDateDialog}
            getDateRangeText={getDateRangeText}
            buttonProps={{
              startIcon: <CalendarTodayIcon />,
              endIcon: <KeyboardArrowDownIcon />,
            }}
          />
        </Box>

        {/* 이번 달 보험 만료 필터 추가 */}
        <Box>
          <Typography
            variant="caption"
            sx={{ mb: 1, color: "#666", display: "block" }}
          >
            보험만료 예정
          </Typography>
          <Button
            variant={currentMonthExpiryFilter ? "contained" : "outlined"}
            size="small"
            onClick={() =>
              setCurrentMonthExpiryFilter(!currentMonthExpiryFilter)
            }
            sx={{
              minWidth: 100,
              height: 40,
              borderColor: currentMonthExpiryFilter ? "#1976d2" : "#E0E0E0",
              backgroundColor: currentMonthExpiryFilter ? "#1976d2" : "white",
              color: currentMonthExpiryFilter ? "white" : "#666",
              "&:hover": {
                backgroundColor: currentMonthExpiryFilter
                  ? "#1565c0"
                  : "rgba(25, 118, 210, 0.04)",
                borderColor: currentMonthExpiryFilter ? "#1565c0" : "#BDBDBD",
              },
            }}
          >
            {currentMonthExpiryFilter ? "이번 달 만료" : "이번 달 만료"}
          </Button>
        </Box>

        {/* 페이지 크기 선택 */}
        <Box>
          <Typography
            variant="caption"
            sx={{ mb: 1, color: "#666", display: "block" }}
          >
            표시 개수
          </Typography>
          <FormControl
            size="small"
            sx={{
              minWidth: 80,
              backgroundColor: "white",
            }}
          >
            <Select
              value={size}
              onChange={handlePageSizeChange}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
              }}
            >
              <MenuItem value={10}>10개</MenuItem>
              <MenuItem value={20}>20개</MenuItem>
              <MenuItem value={50}>50개</MenuItem>
              <MenuItem value={100}>100개</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* 날짜 범위 선택 캘린더 다이얼로그 */}
        <DateRangeCalendar
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
          open={dateDialogOpen}
          onClose={handleCloseDateDialog}
          onApply={handleApplyDateFilter}
          onReset={handleResetDateFilter}
        />
      </Box>

      {/* 계약 목록 */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          mt: 3,
          overflowX: "auto",
        }}
      >
        <Box sx={{ minWidth: "1330px" }}>
          {/* 검색 결과 요약 */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #EEEEEE",
            }}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              전체 {totalElements}건 중 {filteredContracts.length}건 (페이지{" "}
              {page + 1}/{totalPages})
            </Typography>
            {(searchQuery ||
              statusFilter ||
              activeStatusFilter !== "active" ||
              dateFilterActive ||
              currentMonthExpiryFilter) && (
              <Typography variant="body2" sx={{ color: "#1976d2" }}>
                {searchQuery && `검색어: "${searchQuery}" `}
                {statusFilter && `상태: ${statusFilter} `}
                {activeStatusFilter !== "active" &&
                  `활성화: ${
                    activeStatusFilter === "inactive" ? "만료" : "전체"
                  } `}
                {dateFilterActive && `기간: ${getDateRangeText()} `}
                {currentMonthExpiryFilter && `이번 달 보험 만료 `}
              </Typography>
            )}
          </Box>

          {/* 목록 헤더 */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "minmax(230px, 2.2fr) minmax(220px, 2.2fr) minmax(140px, 1.2fr) minmax(250px, 2.2fr) minmax(160px, 1.3fr) minmax(130px, 1fr) minmax(130px, 1fr) minmax(50px, 0.5fr)",
              p: 2,
              borderBottom: "1px solid #EEEEEE",
              backgroundColor: "#F8F9FA",
            }}
          >
            <SortableHeader field="contractNumber">계약번호</SortableHeader>
            <SortableHeader field="title">계약명</SortableHeader>
            <SortableHeader field="statusName" sortable={false}>
              계약상태
            </SortableHeader>
            <SortableHeader field="trusteeName">수탁사업자명</SortableHeader>
            <SortableHeader field="storeTelNumber" sortable={false}>
              매장 전화번호
            </SortableHeader>
            <SortableHeader field="insuranceStartDate">
              보험시작일
            </SortableHeader>
            <SortableHeader field="insuranceEndDate">보험종료일</SortableHeader>
            <SortableHeader field="management" sortable={false}>
              관리
            </SortableHeader>
          </Box>

          {/* 계약 목록 또는 빈 상태 메시지 */}
          {filteredContracts.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">
                검색 조건에 맞는 계약이 없습니다.
              </Typography>
            </Box>
          ) : (
            // 계약 목록 아이템
            filteredContracts.map((contract) => (
              <Box
                key={contract.id}
                onClick={() => handleContractClick(contract.id)}
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(230px, 2.2fr) minmax(220px, 2.2fr) minmax(140px, 1.2fr) minmax(250px, 2.2fr) minmax(160px, 1.3fr) minmax(130px, 1fr) minmax(130px, 1fr) minmax(50px, 0.5fr)",
                  p: 2,
                  borderBottom: "1px solid #EEEEEE",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#F8F9FA" },
                }}
              >
                <Typography sx={{ display: "flex", alignItems: "center" }}>
                  {contract.contractNumber || "-"}
                </Typography>
                <Typography
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    "&:hover": {
                      color: "#1976d2",
                    },
                  }}
                >
                  {contract.title}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {renderStatusChip(contract)}
                </Box>
                <Typography sx={{ display: "flex", alignItems: "center" }}>
                  {contract.companyName || "-"}
                </Typography>
                <Typography sx={{ display: "flex", alignItems: "center" }}>
                  {contract.storeTelNumber || "-"}
                </Typography>
                <Typography sx={{ display: "flex", alignItems: "center" }}>
                  {contract.insuranceStartDate || "-"}
                </Typography>
                <Typography sx={{ display: "flex", alignItems: "center" }}>
                  {contract.insuranceEndDate || "-"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick(e, contract.id);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* 페이지네이션 */}
      {totalElements > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            p: 2,
            backgroundColor: "white",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#666" }}>
            {totalElements > 0
              ? `${page * size + 1}-${Math.min(
                  (page + 1) * size,
                  totalElements
                )}개 / 전체 ${totalElements}개`
              : "검색 결과가 없습니다"}
          </Typography>

          {totalPages > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* 페이지네이션 버튼들 */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {/* 이전 10개 범위 */}
                <IconButton
                  size="small"
                  onClick={() => {
                    const newPage = Math.max(
                      0,
                      Math.floor(page / 10) * 10 - 10
                    );
                    setPage(newPage);
                  }}
                  disabled={page < 10}
                  sx={{
                    borderRadius: 1,
                    border: "1px solid #ddd",
                    "&:disabled": { opacity: 0.4 },
                  }}
                >
                  <KeyboardDoubleArrowLeftIcon fontSize="small" />
                </IconButton>

                {/* 이전 페이지 */}
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  sx={{
                    borderRadius: 1,
                    border: "1px solid #ddd",
                    "&:disabled": { opacity: 0.4 },
                  }}
                >
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>

                {/* 페이지 번호들 (10개씩) */}
                {(() => {
                  const startPage = Math.floor(page / 10) * 10;
                  const endPage = Math.min(startPage + 10, totalPages);
                  const pages = [];
                  for (let i = startPage; i < endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        size="small"
                        variant={page === i ? "contained" : "outlined"}
                        onClick={() => setPage(i)}
                        sx={{
                          minWidth: "36px",
                          height: "36px",
                          borderRadius: 1,
                          ...(page === i
                            ? {
                                backgroundColor: "#1976d2",
                                color: "white",
                                "&:hover": { backgroundColor: "#1565c0" },
                              }
                            : {
                                borderColor: "#ddd",
                                color: "#333",
                                "&:hover": { backgroundColor: "#f5f5f5" },
                              }),
                        }}
                      >
                        {i + 1}
                      </Button>
                    );
                  }
                  return pages;
                })()}

                {/* 다음 페이지 */}
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  sx={{
                    borderRadius: 1,
                    border: "1px solid #ddd",
                    "&:disabled": { opacity: 0.4 },
                  }}
                >
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>

                {/* 다음 10개 범위 */}
                <IconButton
                  size="small"
                  onClick={() => {
                    const newPage = Math.min(
                      totalPages - 1,
                      Math.floor(page / 10) * 10 + 10
                    );
                    setPage(newPage);
                  }}
                  disabled={Math.floor(page / 10) * 10 + 10 >= totalPages}
                  sx={{
                    borderRadius: 1,
                    border: "1px solid #ddd",
                    "&:disabled": { opacity: 0.4 },
                  }}
                >
                  <KeyboardDoubleArrowRightIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* 총 페이지 수 표시 */}
              <Typography variant="body2" sx={{ color: "#666", ml: 1 }}>
                (총 {totalPages}페이지)
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* 계약 관리 메뉴 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {/* 재계약 메뉴 추가 */}
        <MenuItem onClick={handleOpenRenewModal}>재계약</MenuItem>
        {/* 활성화 상태에 따라 비활성화/활성화 버튼 표시 */}
        {selectedContract?.active === false ? (
          [
            <MenuItem key="status-change" onClick={handleOpenStatusChangeDialog}>
              상태변경
            </MenuItem>,
            <MenuItem
              key="activate"
              onClick={handleOpenActivateDialog}
              sx={{ color: "success.main" }}
            >
              활성화
            </MenuItem>,
          ]
        ) : (
          <MenuItem
            onClick={handleOpenCancelDialog}
            sx={{ color: "error.main" }}
          >
            비활성화
          </MenuItem>
        )}
      </Menu>

      {/* 비활성화 확인 다이얼로그 */}
      <Dialog
        open={cancelDialogOpen}
        onClose={!cancelLoading ? handleCloseCancelDialog : undefined}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #F0F0F0",
            py: 2,
            px: 3,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          계약 비활성화
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 3, color: "#505050" }}>
            이 계약을 비활성화하시겠습니까? 비활성화된 계약은 목록에서 숨겨지며,
            서명 링크가 무효화됩니다.
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: "#505050", fontWeight: 500 }}
          >
            변경할 계약 상태
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <Select
              displayEmpty
              value={cancelStatusCode}
              onChange={(e) => setCancelStatusCode(e.target.value)}
              disabled={cancelLoading}
            >
              <MenuItem value="">상태 유지</MenuItem>
              {DEACTIVATE_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.codeId} value={option.codeId}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: "#505050", fontWeight: 500 }}
          >
            비활성화 사유 (선택)
          </Typography>
          <TextField
            autoFocus
            fullWidth
            placeholder="비활성화 사유를 입력해주세요"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#E0E0E0",
                },
                "&:hover fieldset": {
                  borderColor: "#BDBDBD",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#3182F6",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #F0F0F0" }}>
          <Button
            onClick={handleCloseCancelDialog}
            disabled={cancelLoading}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              fontWeight: 500,
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleCancelContract}
            variant="contained"
            disabled={cancelLoading}
            sx={{
              bgcolor: "#d32f2f",
              "&:hover": {
                bgcolor: "#b71c1c",
              },
              fontWeight: 500,
              boxShadow: "none",
            }}
          >
            {cancelLoading ? "처리 중..." : "비활성화"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 활성화 확인 다이얼로그 */}
      <Dialog
        open={activateDialogOpen}
        onClose={!activateLoading ? handleCloseActivateDialog : undefined}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #F0F0F0",
            py: 2,
            px: 3,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          계약 활성화
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body2" sx={{ color: "#505050" }}>
            이 계약을 다시 활성화하시겠습니까? 활성화된 계약은 목록에 표시되며,
            서명 링크가 유효해집니다.
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 3, mb: 1, color: "#505050", fontWeight: 500 }}
          >
            {isClosedStatusContract
              ? "되돌릴 계약 상태 (필수)"
              : "변경할 계약 상태"}
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={activateStatusCode}
              onChange={(e) => setActivateStatusCode(e.target.value)}
              disabled={activateLoading}
            >
              {isClosedStatusContract ? (
                <MenuItem value="" disabled>
                  상태를 선택해주세요
                </MenuItem>
              ) : (
                <MenuItem value="">상태 유지</MenuItem>
              )}
              {ACTIVATE_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.codeId} value={option.codeId}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #F0F0F0" }}>
          <Button
            onClick={handleCloseActivateDialog}
            disabled={activateLoading}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              fontWeight: 500,
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleActivateContract}
            variant="contained"
            disabled={
              activateLoading || (isClosedStatusContract && !activateStatusCode)
            }
            sx={{
              bgcolor: "#2e7d32",
              "&:hover": {
                bgcolor: "#1b5e20",
              },
              fontWeight: 500,
              boxShadow: "none",
            }}
          >
            {activateLoading ? "처리 중..." : "활성화"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 비활성화된 계약 상태 변경 다이얼로그 */}
      <Dialog
        open={statusChangeDialogOpen}
        onClose={
          !statusChangeLoading ? handleCloseStatusChangeDialog : undefined
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #F0F0F0",
            py: 2,
            px: 3,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          계약 상태 변경
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 3, color: "#505050" }}>
            비활성화된 계약의 상태를 변경합니다.
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: "#505050", fontWeight: 500 }}
          >
            변경할 계약 상태
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={statusChangeCode}
              onChange={(e) => setStatusChangeCode(e.target.value)}
              disabled={statusChangeLoading}
            >
              <MenuItem value="" disabled>
                상태를 선택해주세요
              </MenuItem>
              {DEACTIVATE_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.codeId} value={option.codeId}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #F0F0F0" }}>
          <Button
            onClick={handleCloseStatusChangeDialog}
            disabled={statusChangeLoading}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              fontWeight: 500,
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleChangeClosedStatus}
            variant="contained"
            disabled={
              statusChangeLoading ||
              !statusChangeCode ||
              statusChangeCode === selectedContract?.statusCodeId
            }
            sx={{
              bgcolor: "#3182F6",
              "&:hover": {
                bgcolor: "#1B64DA",
              },
              fontWeight: 500,
              boxShadow: "none",
            }}
          >
            {statusChangeLoading ? "처리 중..." : "저장"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 재계약 모달 */}
      <ContractRenewModal
        open={renewModalOpen}
        onClose={handleCloseRenewModal}
        contract={selectedContract}
        company={selectedCompany}
        onSuccess={handleRenewSuccess}
      />

      {/* 알림 스낵바 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractList;
